/** Machine-readable failure codes the UI branches on. */
export type ApiErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'network'
  | 'unknown';

export interface ApiErrorDetail {
  readonly field: string;
  readonly message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: readonly ApiErrorDetail[];
  /**
   * The backend's raw `error.code`, preserved even when it maps to no known
   * `ApiErrorCode`, so a call site can branch on a domain-specific code the
   * transport does not model.
   */
  readonly serverCode: string | null;

  constructor(
    message: string,
    options: {
      status: number;
      code: ApiErrorCode;
      details?: readonly ApiErrorDetail[];
      serverCode?: string | null;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details ?? [];
    this.serverCode = options.serverCode ?? null;
  }

  static fromStatus(
    status: number,
    message: string,
    details?: readonly ApiErrorDetail[],
    serverCode?: string | null,
  ): ApiError {
    return new ApiError(message, {
      status,
      // The server's own code is authoritative when we recognise it; status is
      // the fallback for transports and proxies that never set one.
      code: codeFromServer(serverCode) ?? codeForStatus(status),
      details,
      serverCode,
    });
  }

  static network(message = 'Unable to reach the server.'): ApiError {
    return new ApiError(message, { status: 0, code: 'network' });
  }

  get isAuthFailure(): boolean {
    return this.code === 'unauthorized' || this.code === 'forbidden';
  }
}

/**
 * Backend `error.code` values, mapped onto the client union.
 *
 * The server's catalog is `ErrorCode` in `core/errors/app-error.ts`; every
 * member of it is covered here. Keys are matched case-insensitively so the
 * wire spelling (`VALIDATION_FAILED`) and a lowercase variant both resolve.
 */
const SERVER_CODES: Readonly<Record<string, ApiErrorCode>> = {
  bad_request: 'validation',
  validation_failed: 'validation',
  unauthenticated: 'unauthorized',
  forbidden: 'forbidden',
  not_found: 'not_found',
  conflict: 'conflict',
  // No dedicated client code: surfaced as a generic failure the UI can retry.
  rate_limited: 'unknown',
  upstream_failure: 'server',
  internal: 'server',
};

function codeFromServer(serverCode?: string | null): ApiErrorCode | null {
  if (!serverCode) return null;
  return SERVER_CODES[serverCode.toLowerCase()] ?? null;
}

function codeForStatus(status: number): ApiErrorCode {
  switch (status) {
    // The backend returns 400 for field validation failures; 422 is kept so
    // the client behaves the same whichever status it is handed.
    case 400:
    case 422:
      return 'validation';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    default:
      return status >= 500 ? 'server' : 'unknown';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
